import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LeafletDirective, LeafletModule } from '@bluehalo/ngx-leaflet';
import { LightboxModule } from 'ngx-lightbox';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { QuillModule } from 'ngx-quill';

import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from './login/login.component';
import { AppLayoutComponent } from './app-layout/app-layout.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ErrorInterceptor } from './shared/_helpers/error.interceptor';
import { AuthGuard } from './shared/_helpers/auth.guard';
import { JwtInterceptor } from './shared/_helpers/jwt.interceptor';
import { UpdateOrganizationComponent } from './organizations/update-organization/update-organization.component';
import { SharedService } from './shared/shared.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrganizationsComponent } from './organizations/organizations.component';
import { ConfirmModalComponent } from './shared/confirm-modal/confirm-modal.component';
import { ToastComponent } from './shared/toast/toast.component';
import { UpdateScheduleComponent } from './organizations/update-schedule/update-schedule.component';
import { ScheduleItemComponent } from './organizations/update-schedule/schedule-item/schedule-item.component';
import { CounterComponent } from './user/counter/counter.component';
import { ReservationsComponent } from './user/reservations/reservations.component';
import { UserMenuComponent } from './user/user-menu/user-menu.component';
import { ReservationItemComponent } from './user/reservations/simple-reservations/reservation-item/reservation-item.component';
import { ReservationStatusPipe } from './user/reservations/simple-reservations/reservation-item/reservation-status.pipe';
import { DynamicDataComponent } from './user/dynamic-data/dynamic-data.component';
import { UpdatePricesComponent } from './organizations/update-prices/update-prices.component';
import { OrganizationMenuComponent } from './organizations/organization-menu/organization-menu.component';
import { LoadingSpinnerComponent } from './shared/loading-spinner/loading-spinner.component';
import { UpdateFacilitiesComponent } from './organizations/update-facilities/update-facilities.component';
import { UpdateCoursesComponent } from './organizations/update-courses/update-courses.component';
import { CourseItemComponent } from './organizations/update-courses/course-item/course-item.component';
import { UpdateLidoFacilitiesComponent } from './organizations/update-lido-facilities/update-lido-facilities.component';
import { environment } from 'src/environments/environment';
import { PushNotificationComponent } from './organizations/push-notification/push-notification.component';
import { SlotReservationsComponent } from './user/reservations/slot-reservations/slot-reservations.component';
import { SlotReservatonItemComponent } from './user/reservations/slot-reservations/slot-reservaton-item/slot-reservaton-item.component';
import { SimpleReservationsComponent } from './user/reservations/simple-reservations/simple-reservations.component';
import { SlotTableItemComponent } from './user/reservations/slot-reservations/slot-table-item/slot-table-item.component';
import { VssOrganizationsComponent } from './vss/vss-organizations/vss-organizations.component';
import { VssAppLayoutComponent } from './vss/vss-app-layout/vss-app-layout.component';
import { VssUpdateOrganizationComponent } from './vss/vss-update-organization/vss-update-organization.component';
import { VssOrganizationMenuComponent } from './vss/vss-organization-menu/vss-organization-menu.component';
import { UpdateTrainingSessionsComponent } from './vss/update-training-sessions/update-training-sessions.component';
import { VssFilesUploadComponent } from './vss/vss-files-upload/vss-files-upload.component';
import { VssEventsComponent } from './vss/vss-events/vss-events.component';
import { VssUpdateEventComponent } from './vss/vss-events/vss-update-event/vss-update-event.component';
import { TrainingSessionScheduleItemComponent } from './vss/update-training-sessions/training-session-schedule-item/training-session-schedule-item.component';
import { EventsComponent } from './organizations/events/events.component';
import { UpdateEventComponent } from './organizations/events/update-event/update-event.component';

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

const routes: Routes = [
  {
    path: '', component: AppLayoutComponent, children: [
      { path: '', component: OrganizationsComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/informations', component: UpdateOrganizationComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/schedules', component: UpdateScheduleComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/prices', component: UpdatePricesComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/facilities', component: UpdateFacilitiesComponent, canActivate: [AuthGuard] },
      {
        path: 'organization/:orgId/courses',
        component: (environment.useOldLidoFacilities ? UpdateLidoFacilitiesComponent : UpdateCoursesComponent),
        canActivate: [AuthGuard]
      },
      // { path: 'organization/:orgId/events', component: UpdateCoursesComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/events', component: EventsComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/events/create', component: UpdateEventComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/events/:eventId/update', component: UpdateEventComponent, canActivate: [AuthGuard] },
      /* *** */
      { path: 'organization/:orgId/push-notification', component: PushNotificationComponent, canActivate: [AuthGuard] },
      { path: 'user/counter/:orgId', component: CounterComponent, canActivate: [AuthGuard] },
      { path: 'user/reservations/:orgId', component: ReservationsComponent, canActivate: [AuthGuard] },
      { path: 'user/dynamic-data/:orgId', component: DynamicDataComponent, canActivate: [AuthGuard] },
      { path: 'user/push-notification/:orgId', component: PushNotificationComponent, canActivate: [AuthGuard] },
      // { path: '**', component: DashboardComponent }
    ]
  },
  {
    path: 'vss', component: VssAppLayoutComponent, children: [
      { path: '', component: VssOrganizationsComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/informations', component: VssUpdateOrganizationComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/push-notification', component: PushNotificationComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/news', component: DynamicDataComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/training-sessions', component: UpdateTrainingSessionsComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/events', component: VssEventsComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/events/create', component: VssUpdateEventComponent, canActivate: [AuthGuard] },
      { path: 'organization/:orgId/events/:eventId/update', component: VssUpdateEventComponent, canActivate: [AuthGuard] },
    ]
  },
  { path: 'login', component: LoginComponent },
];


@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        AppLayoutComponent,
        UpdateOrganizationComponent,
        OrganizationsComponent,
        ConfirmModalComponent,
        ToastComponent,
        UpdateScheduleComponent,
        ScheduleItemComponent,
        CounterComponent,
        ReservationsComponent,
        UserMenuComponent,
        ReservationItemComponent,
        ReservationStatusPipe,
        DynamicDataComponent,
        UpdatePricesComponent,
        OrganizationMenuComponent,
        LoadingSpinnerComponent,
        UpdateFacilitiesComponent,
        UpdateCoursesComponent,
        CourseItemComponent,
        UpdateLidoFacilitiesComponent,
        PushNotificationComponent,
        SlotReservationsComponent,
        SlotReservatonItemComponent,
        SimpleReservationsComponent,
        SlotTableItemComponent,
        VssOrganizationsComponent,
        VssAppLayoutComponent,
        VssUpdateOrganizationComponent,
        VssOrganizationMenuComponent,
        UpdateTrainingSessionsComponent,
        VssFilesUploadComponent,
        VssEventsComponent,
        VssUpdateEventComponent,
        TrainingSessionScheduleItemComponent,
        EventsComponent,
        UpdateEventComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        BrowserAnimationsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        RouterModule.forRoot(routes),
        NgbModule,
        FormsModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        LeafletModule,
        LeafletDirective,
        LightboxModule,
        QuillModule.forRoot({
            modules: {
                syntax: false,
                toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'align': ['', 'right', 'center', 'justify'] }],
                    ['link']
                ]
            }
        })], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        SharedService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
